import { base64, hex } from "@scure/base";
import { Address, NETWORK, OutScript, Transaction, p2tr } from "@scure/btc-signer";
import { tapLeafHash } from "@scure/btc-signer/payment.js";
import { taprootTweakPubkey } from "@scure/btc-signer/utils.js";
import { schnorr } from "@noble/curves/secp256k1.js";
import { parseWitness } from "micro-ordinals";
import type { SignedPackage } from "./types";

export const REFUND_MIN_RATE = 1;
export const REFUND_MAX_RATE = 100;
export const REFUND_MAX_FEE = 10_000;
export const REFUND_DUST = 546;
export type RefundDestination = "payment" | "ordinals";
export interface RecoveryWallet {
  paymentAddress: string;
  ordinalsAddress: string;
  ordinalsPublicKey: string;
}
export interface RefundSource {
  txid: string;
  index: number;
  value: number;
  script: string;
  internalKey: string;
  merkleRoot: string;
}
export interface RefundQuote {
  source: RefundSource;
  destination: RefundDestination;
  address: string;
  feeRate: number;
  vsize: number;
  minerFee: number;
  refundAmount: number;
  psbt: string;
  authorization: string;
  expiresAt: number;
}
const same = (a: Uint8Array, b: Uint8Array) => hex.encode(a) === hex.encode(b);

export function recoveryTransaction(raw: string): Transaction {
  if (!raw || raw.length > 2_000_000 || raw.length % 2 || !/^[0-9a-f]+$/i.test(raw)) throw new Error("Invalid recovery transaction");
  try {
    return Transaction.fromRaw(hex.decode(raw), { allowUnknownInputs: true, allowUnknownOutputs: true, disableScriptCheck: true });
  } catch { throw new Error("Invalid recovery transaction"); }
}

/** Single-leaf gallery commits only. All metadata is reconstructed, never supplied separately. */
export function reconstructRefundSource(saved: SignedPackage, wallet: RecoveryWallet): RefundSource {
  const commit = recoveryTransaction(saved.commitHex);
  const reveal = recoveryTransaction(saved.revealHex);
  if (commit.id !== saved.commitTxid || reveal.id !== saved.revealTxid) throw new Error("Recovery transaction IDs do not match");
  const keyBytes = hex.decode(wallet.ordinalsPublicKey);
  const internalKey = keyBytes.length === 33 && (keyBytes[0] === 2 || keyBytes[0] === 3) ? keyBytes.slice(1) : keyBytes;
  if (internalKey.length !== 32 || p2tr(internalKey).address !== wallet.ordinalsAddress) throw new Error("The ordinals public key does not match this wallet");
  const inputs = Array.from({ length: reveal.inputsLength }, (_, i) => reveal.getInput(i))
    .filter(input => input.txid && hex.encode(input.txid) === commit.id);
  if (inputs.length !== 1) throw new Error("This recovery requires exactly one saved commit output; this package is not supported");
  const input = inputs[0]!;
  const witness = input.finalScriptWitness;
  if (!witness || witness.length !== 3) throw new Error("The saved gallery reveal witness is incomplete");
  const script = witness[1]!;
  const control = witness[2]!;
  if (control.length !== 33 || (control[0]! & 0xfe) !== 0xc0 || !same(control.slice(1), internalKey)) {
    throw new Error("The saved commit tree does not belong to the authenticated ordinals key");
  }
  const inscriptions = parseWitness(witness);
  if (!inscriptions?.length) {
    throw new Error("The saved reveal has no inscription envelope");
  }
  const merkleRoot = tapLeafHash(script, 0xc0);
  const [outputKey, parity] = taprootTweakPubkey(internalKey, merkleRoot);
  if ((control[0]! & 1) !== parity) throw new Error("Invalid saved Taproot control block");
  const expectedScript = OutScript.encode({ type: "tr", pubkey: outputKey });
  const index = input.index;
  if (index === undefined || index >= commit.outputsLength) throw new Error("Invalid saved commit outpoint");
  const output = commit.getOutput(index);
  if (!output.script || !same(output.script, expectedScript) || !output.amount || output.amount > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error("The saved commit value or script does not match its Taproot tree");
  }
  return { txid: commit.id, index, value: Number(output.amount), script: hex.encode(expectedScript), internalKey: hex.encode(internalKey), merkleRoot: hex.encode(merkleRoot) };
}

export function buildRefund(source: RefundSource, wallet: RecoveryWallet, destination: RefundDestination, feeRate: number) {
  if (destination !== "payment" && destination !== "ordinals") throw new Error("Choose the connected payment or ordinals address");
  if (!Number.isFinite(feeRate) || feeRate < REFUND_MIN_RATE || feeRate > REFUND_MAX_RATE) throw new Error(`Refund fee rate must be ${REFUND_MIN_RATE}–${REFUND_MAX_RATE} sat/vB`);
  const address = destination === "payment" ? wallet.paymentAddress : wallet.ordinalsAddress;
  const decoded = Address(NETWORK).decode(address);
  if (!["wpkh", "sh", "tr"].includes(decoded.type)) throw new Error("Refund destination must be the connected SegWit or Taproot wallet");
  const internalKey = hex.decode(source.internalKey);
  const merkleRoot = hex.decode(source.merkleRoot);
  const [key] = taprootTweakPubkey(internalKey, merkleRoot);
  if (hex.encode(OutScript.encode({ type: "tr", pubkey: key })) !== source.script || !Number.isSafeInteger(source.value) || source.value <= 0) throw new Error("Invalid refund source");
  const transaction = new Transaction({ version: 2, lockTime: 0 });
  transaction.addInput({ txid: source.txid, index: source.index, sequence: 0xffffffff,
    witnessUtxo: { script: hex.decode(source.script), amount: BigInt(source.value) },
    tapInternalKey: internalKey, tapMerkleRoot: merkleRoot, sighashType: 0,
    // BIP371 derivation fields are optional for a software signer selected by address.
    // Never invent a hardware wallet fingerprint/path or include tapLeafScript.
  });
  transaction.addOutputAddress(address, BigInt(source.value));
  const sized = transaction.clone();
  sized.updateInput(0, { finalScriptWitness: [new Uint8Array(64)] });
  const vsize = sized.vsize;
  const minerFee = Math.ceil(vsize * feeRate);
  const refundAmount = source.value - minerFee;
  if (minerFee > REFUND_MAX_FEE) throw new Error("Refund miner fee exceeds the 10,000-sat safety cap; choose a lower rate");
  if (refundAmount < REFUND_DUST) throw new Error("The refund would be below the 546-sat dust minimum");
  transaction.updateOutput(0, { amount: BigInt(refundAmount) });
  return { transaction, address, feeRate, vsize, minerFee, refundAmount };
}

/** Checks the entire unsigned transaction and the BIP341 key-path signature. */
export function validateSignedRefund(raw: string, source: RefundSource, wallet: RecoveryWallet, destination: RefundDestination, rate: number): Transaction {
  const expected = buildRefund(source, wallet, destination, rate);
  const signed = recoveryTransaction(raw);
  if (signed.inputsLength !== 1 || signed.outputsLength !== 1 || !same(signed.unsignedTx, expected.transaction.unsignedTx)) throw new Error("The wallet changed the refund transaction");
  const input = signed.getInput(0);
  const witness = input.finalScriptWitness;
  if (input.finalScriptSig?.length || !witness || witness.length !== 1 || witness[0]?.length !== 64) throw new Error("Refund requires a Taproot key-path signature only; script-path spending is forbidden");
  const hash = signed.preimageWitnessV1(0, [hex.decode(source.script)], 0, [BigInt(source.value)]);
  if (!schnorr.verify(witness[0], hash, hex.decode(source.script).slice(2))) throw new Error("Invalid refund wallet signature");
  if (signed.vsize !== expected.vsize || source.value - Number(signed.getOutput(0).amount) !== Math.ceil(signed.vsize * rate)) throw new Error("The exact signed refund fee differs from the approved fee");
  return signed;
}

export function finalizeRefundPsbt(signedPsbt: Uint8Array, quote: RefundQuote, wallet: RecoveryWallet): string {
  let transaction: Transaction;
  try {
    transaction = Transaction.fromPSBT(signedPsbt);
    if (transaction.inputsLength !== 1 || transaction.outputsLength !== 1) throw new Error();
    const input = transaction.getInput(0);
    if (input.tapLeafScript?.length || input.tapScriptSig?.length) throw new Error();
    if (!input.finalScriptWitness) transaction.finalize();
  } catch { throw new Error("This wallet could not sign the commit’s Taproot key-path refund. No refund was sent."); }
  const raw = hex.encode(transaction.extract());
  validateSignedRefund(raw, quote.source, wallet, quote.destination, quote.feeRate);
  return raw;
}

export const refundPsbtBase64 = (transaction: Transaction) => base64.encode(transaction.toPSBT(0));
